import { computed, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import {
  parseApplicationSource,
  resolveApplicationSource,
} from '../../../../../root/application-source/application-source.util';
import {
  Applicant,
  DiversityInformationJobOpportunity,
  ExtraInformationJobOpportunity,
  HowFindJobOpportunityEnum,
  JobOpportunityTenantId,
  ProfessionalExperienceCompany,
  Qualification,
  ResumeListTypeEnum,
} from '@models/index';
import { ApplicantLinkedinStateService } from './applicant-linkedin-state.service';
import { StringUtils } from 'ats-ui-lib';
import { InviteTalentBankStorageServiceService } from '@services/talent-bank/invite-talent-bank-storage-service.service';
import { InviteJobApplicationStorageService } from '@services/invite-job-application-storage/invite-job-application-storage.service';
import { QrCodeTrackingCookieService } from '@services/qr-code/qr-code-tracking-cookie.service';

const CUSTOM_HEADER_HEIGHT = 130;

export type ExpandedResumeSection = 'experiences' | 'qualifications' | 'languages' | 'none';

interface SavedCurriculumSteps {
  profile: boolean;
  aboutMe: boolean;
  experiences: boolean;
  qualifications: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ResumeApplicantService {
  public applicant = signal<Applicant>(window.history.state?.applicant);

  public jobApplicationStarted = signal<JobOpportunityTenantId | null>(null);

  public applicantLinkedin = computed(() => this.applicantLinkedinService.updatedData());

  private linkedinBirthday = computed(() => this.applicantLinkedin()?.personalData?.birthday);

  public extraInformation = signal<ExtraInformationJobOpportunity>(
    <ExtraInformationJobOpportunity>{},
  );

  public diversityInformation = signal<DiversityInformationJobOpportunity>(
    <DiversityInformationJobOpportunity>{},
  );

  public get isJobApplication(): boolean {
    return window.history.state.isJobApplication;
  }

  public get isTalentBankInvite(): boolean {
    return !!this.inviteTalentBankStorageService.getInvite();
  }

  public get isJobApplicationInvite(): boolean {
    return !!this.inviteJobApplicationStorageService.getInvite();
  }

  get usingImport(): boolean {
    return window.history.state.usingImport;
  }

  get jobOpportunityId(): string {
    return window.history.state.jobOpportunityId;
  }

  public get tenantId(): string {
    return window.history.state.tenantId;
  }

  public anyBirthday = computed<boolean>(() => {
    let birthday = this.applicant()?.personalData?.birthday;
    const birthdayLinkedin = this.linkedinBirthday() ?? '';

    if (birthdayLinkedin && !birthday) {
      birthday = birthdayLinkedin;
    }

    return StringUtils.isTextFilled(birthday);
  });

  public isResumeImported = computed(() => {
    return this.applicantLinkedin() !== null;
  });

  private navigationEnd = toSignal(
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)),
  );

  public wasUpdatedAtLeastOnce = computed(() => {
    this.navigationEnd();
    return this.anyBirthday();
  });

  public wasUpdatedAtLeastOnceInvitationFlow = computed(() => {
    this.navigationEnd();
    return !this.isJobApplication && this.isTalentBankInvite && !this.anyBirthday();
  });

  public disableEdit = computed(() => {
    this.navigationEnd();
    return (
      (this.isJobApplication || this.usingImport || this.isTalentBankInvite) && !this.anyBirthday()
    );
  });

  public isJobApplicationFlow = computed(() => {
    this.navigationEnd();
    return this.isJobApplication;
  });

  public jobOpportunityTitle = computed(() => {
    if (this.isJobApplicationFlow()) return window.history.state.jobOpportunityTitle;
    return '';
  });

  public expandedResumeSection = computed((): ExpandedResumeSection | undefined => {
    this.navigationEnd();
    return window.history.state?.expandedResumeSection;
  });

  public hideLinkedinMessage = computed(() => {
    return Object.values(this.steps()).every((isStepSaved) => isStepSaved);
  });

  public steps = signal<SavedCurriculumSteps>({
    profile: false,
    aboutMe: false,
    experiences: false,
    qualifications: false,
  });

  constructor(
    private router: Router,
    private applicantLinkedinService: ApplicantLinkedinStateService,
    private inviteTalentBankStorageService: InviteTalentBankStorageServiceService,
    private inviteJobApplicationStorageService: InviteJobApplicationStorageService,
    private qrCodeTrackingCookieService: QrCodeTrackingCookieService,
  ) {}

  public applyApplicationSourceFromQueryParam(
    rawApplicationSource: string | number | null | undefined,
  ): void {
    if (!this.isJobApplication) return;

    const jobOpportunityId = this.jobOpportunityId;
    const current = this.getExtraInformationBy(jobOpportunityId);
    if (current?.howFindJobOpportunity != null) return;

    const howFindJobOpportunity = resolveApplicationSource(
      parseApplicationSource(rawApplicationSource),
      this.resolveInviteApplicationSource(jobOpportunityId),
    );
    if (howFindJobOpportunity == null) return;

    this.extraInformation.set({
      jobOpportunityId,
      howFindJobOpportunity,
      otherChannels: current?.otherChannels ?? null,
      isRecommendation: current?.isRecommendation ?? null,
      corporateEmailRecommendation: current?.corporateEmailRecommendation ?? null,
      currentlyWorksAtThisCompany: current?.currentlyWorksAtThisCompany ?? null,
    });
  }

  /**
   * When the candidate reaches the application through an invite or a QR Code, that origin
   * takes precedence over a plain career page visit since it identifies the source more precisely.
   */
  private resolveInviteApplicationSource(
    jobOpportunityId: string,
  ): HowFindJobOpportunityEnum | null {
    if (this.isJobApplicationInvite) return HowFindJobOpportunityEnum.invitedToJobOpportunity;
    if (this.isTalentBankInvite) return HowFindJobOpportunityEnum.addedFromTalentBank;
    if (this.qrCodeTrackingCookieService.getQrCodeId(jobOpportunityId))
      return HowFindJobOpportunityEnum.qrCode;

    return null;
  }

  public updateResume(resume: Partial<Applicant>, savedStep: keyof SavedCurriculumSteps): void {
    if (this.applicantLinkedin() !== null) {
      this.applicantLinkedinService.updateData(resume);
      if (savedStep) this.markStepAsSaved(savedStep);
    }
  }

  public updateResumeList(
    list: ProfessionalExperienceCompany[] | Qualification[],
    type: ResumeListTypeEnum,
  ): void {
    if (this.applicantLinkedin() !== null) {
      this.applicantLinkedinService.updateList(list, type);
      const key = type === ResumeListTypeEnum.Qualification ? 'qualifications' : 'experiences';
      this.markStepAsSaved(key);
    }
  }

  private markStepAsSaved(step: keyof SavedCurriculumSteps): void {
    const current = this.steps();
    const updated = { ...current, [step]: true };
    this.steps.set(updated);
  }

  public getExtraInformationBy(jobOpportunityId: string): ExtraInformationJobOpportunity | null {
    if (this.extraInformation().jobOpportunityId === jobOpportunityId)
      return this.extraInformation();
    return null;
  }

  public getDiversityInformationBy(
    jobOpportunityId: string,
  ): DiversityInformationJobOpportunity | null {
    if (this.diversityInformation().jobOpportunityId === jobOpportunityId)
      return this.diversityInformation();
    return null;
  }

  public calculateFooterHeight(height: string): string {
    if (this.isJobApplication) return `${CUSTOM_HEADER_HEIGHT + +height.replace('px', '')}px`;
    return height;
  }

  public async navigateToResumeList(options?: {
    expandedResumeSection?: ExpandedResumeSection;
  }): Promise<void> {
    const state = {
      applicant: this.applicant(),
      usingImport: this.isResumeImported(),
      ...(options?.expandedResumeSection !== undefined && {
        expandedResumeSection: options.expandedResumeSection,
      }),
    };

    if (this.isJobApplication) {
      this.jobApplicationStarted.set({
        jobOpportunityId: this.jobOpportunityId,
        tenantId: this.tenantId,
      });
      await this.router.navigate(
        ['main', 'applicant', 'job-opportunity-application', this.jobOpportunityId],
        {
          queryParamsHandling: 'merge',
          state,
        },
      );
    } else {
      await this.router.navigate(['main', 'applicant', 'registration'], {
        state,
      });
    }
  }

  public async navigate(
    isJobApplication: boolean,
    route: string[],
    stateParams: any,
  ): Promise<void> {
    if (isJobApplication) {
      this.jobApplicationStarted.set({
        jobOpportunityId: this.jobOpportunityId,
        tenantId: this.tenantId,
      });
      await this.router.navigate(['main', 'applicant', 'job-opportunity-application', ...route], {
        queryParamsHandling: 'merge',
        state: { ...stateParams, isJobApplication: true },
      });
    } else {
      await this.router.navigate(['main', 'applicant', ...route], {
        state: { ...stateParams, isJobApplication: false },
      });
    }
  }
}
